import { Component, OnInit, ElementRef } from '@angular/core';
import { MapsAPILoader, AgmInfoWindow } from '@agm/core';
import { Store } from '@ngrx/store';
import { Observable, BehaviorSubject } from 'rxjs';

import { BarrierNamePipe } from '../shared';
import { DatePipe } from '@angular/common';

import { MapService } from '../core/services/map.service';
import * as mapboxgl from 'mapbox-gl';

import { ObstacleModel, LeaderboardUserModel, FilterModel, TypesFilter } from '../core/models';
import { State } from '../core/store/reducers';
import { GetCountryLeaderboards, GetLeaderboard } from '../core/store/actions/leaderboard.actions';
import { barrierTypes } from '../core/types';
import { CoreoService, ApiService } from '../core/services';
import { Http } from '@angular/http';
import { ToggleType, UpdateCountry } from '../core/store/actions/filter.actions';
import { getLeaderboardLoading,  getFilterCountry, getFilterFilter, getFilterTypes, getLeaderboardUsers, getCountryLeaderboardsLoading, getCountryLeaderboard } from '../core/store/selectors/selectors';


type leaderboardMode = ('users' | 'countries');
type boundaryMode = ('none' | 'regions' | 'countries');
type mapStyle = ('streets' | 'satellite' | 'hybrid');
type rasterMode = ('atlas' | 'model' | 'none');

@Component({
  selector: 'amber-map',
  template: require('./amber-map.component.html'),
  styles: [require('./amber-map.component.scss')]
})


export class AmberMapComponent implements OnInit {
  REGION_ID_PATH = 'C_ZHYD_250';
  COUNTRY_PATH = 'NAME';
  BOUNDARIES_EXPORT_FUNCTION_URL = 'https://europe-west1-amber-barrier-tracker.cloudfunctions.net/generate';
  PROJECT_EXPORT_URL = 'https://europe-west1-amber-barrier-tracker.cloudfunctions.net/generateAllDataExport';
  COUNTRIES_COLLECTION_ID = 433;
  REGIONS_COLLECTION_ID = 306;

  lastOpened: AgmInfoWindow = null;
  bounds = null;
  zoom = 3;

  map: mapboxgl.Map;
  boundaryMode: boundaryMode = 'none';
  leaderboardMode: leaderboardMode = 'users';
  rasterMode: rasterMode = 'none';
  atlasLayerIsOn = false;
  selectedMapStyle: mapStyle = 'streets';
  stylesMenuIsOn = false;
  layerMenuIsOn = false;

  atlasTypes = {
    weir: 'weir',
    dam: 'dams',
    culvert: 'culvert',
    ford: 'ford',
    sluice_gate: 'sluice',
    ramp: 'ramp',
    other: 'other'
  };

  features;
  marker;
  popUp: mapboxgl.Popup;

  // Default position vaguely central Europe
  latitude = 50;
  longitude = 9;

  countryLeaderboard$: Observable<any[]>;
  countryLeaderboardsLoading$: Observable<Boolean>;
  filter$: Observable<FilterModel>;
  filterTypes$: Observable<TypesFilter>;
  selectedCountry$: Observable<String>;
  recent$: Observable<ObstacleModel[]>;
  recentLoading$: Observable<Boolean>;
  leaderboard$: Observable<LeaderboardUserModel[]>;
  another$: Observable<boolean>;
  leaderboardLoading$: Observable<Boolean>;
  bounds$: Observable<any>;
  mapLoaded$: Observable<void>;
  resetZoom$: BehaviorSubject<void> = new BehaviorSubject<void>(null);

  isGeneratingDownload = false;
  hasSubmittedDownloadDetails = false;
  showDownloadThanks = false;

  filterTypes = barrierTypes;
  selectedBoundaries = {};
  highlightsSource = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  } as mapboxgl.GeoJSONSourceRaw;
  highlightFeatures = [];
  filtered = false;
  showFilters = false;

  countries = [
    'Albania', 'Andorra', 'Austria', 'Belgium', 'Bosnia and Herzegovina',
    'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France',
    'Germany', 'Greece', 'Hungary', 'Iceland', 'Ireland', 'Italy', 'Latvia',
    'Lithuania', 'Luxembourg', 'Macedonia', 'Moldavia', 'Montenegro', 'Netherlands',
    'Norway', 'Poland', 'Portugal', 'Republic of Sprska', 'Romania', 'Serbia', 'Slovakia', 'Slovenia',
    'Spain', 'Sweden', 'Switzerland', 'United Kingdom'
  ];

  selected = null;
  zoomControlOptions = null;
  showReset = false;
  hovering = null;

  constructor(
    public datePipe: DatePipe,
    public barrierNamePipe: BarrierNamePipe,
    private mapService: MapService,
    private mapsApi: MapsAPILoader,
    private store: Store<State>,
    private coreo: CoreoService,
    private api: ApiService,
    private http: Http,
    private elRef: ElementRef
  ) {}

  ngOnInit() {
    this.atlasLayerIsOn = this.elRef.nativeElement.getAttribute('atlas') === 'true';

    // Instantiate map using Mapbox Studio defined map style
    this.map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/natapp/ck629fnm00upz1imwyk3i2l46',
      center: [this.longitude, this.latitude],
      zoom: 4.01,
      minZoom: 4.01,
      scrollZoom: true
    });

    // Initialise map features/layers
    this.initializeMap();

    this.features = this.map.querySourceFeatures('composite', {
      sourceLayer: 'amber'
    });

    this.mapLoaded$ = Observable.fromPromise(this.mapsApi.load());
    this.recent$ = this.store.select(state => state.obstacles.recentObstacles);
    this.recentLoading$ = this.store.select(state => state.obstacles.loading);
    this.filter$ = this.store.select(getFilterFilter);
    this.filterTypes$ = this.store.select(getFilterTypes);
    this.selectedCountry$ = this.store.select(getFilterCountry);
    this.filter$.subscribe(filter => {
      // map is filtered if country is set or any barrier type is disabled
      this.filtered = !!filter.country || !Object.keys(<Object>filter.types).every(k => !!filter.types[k]);
      this.mapboxFilter().then(() => {
        if (this.atlasLayerIsOn) {
          this.setAtlasLayersVisibility();
        }
      });
    });
    this.store.dispatch(new GetCountryLeaderboards);
    this.countryLeaderboard$ = this.store.select(getCountryLeaderboard).map(leaderboard => {
      const COUNTRY_LEADERBOARD_SIZE = 9;
      return Object.keys(leaderboard).map(country => ({
        country,
        count: leaderboard[country]
      })).sort((a, b) => {
        if (a.count < b.count) {
          return 1;
        } else if (a.count > b.count) {
          return - 1
        } else {
          return 0;
        }
      }).slice(0, COUNTRY_LEADERBOARD_SIZE);
    });
    this.countryLeaderboardsLoading$ = this.store.select(getCountryLeaderboardsLoading);
    this.countryLeaderboardsLoading$.subscribe();
    this.countryLeaderboard$.subscribe();
    this.store.dispatch(new GetLeaderboard());
    this.leaderboard$ = this.store.select(getLeaderboardUsers);
    this.leaderboardLoading$ = this.store.select(getLeaderboardLoading);
    this.hasSubmittedDownloadDetails = localStorage.getItem('amber-download-cookie') !== null;
    document.body.addEventListener('downloadSubscribe', () => {
      this.onDownloadForm();
    });
  }

  initializeMap() {
    // Initialise marker hover behaviour
    this.map.on('load', () => {

      let markerMaker = (type) => {
        let marker: any = document.createElement('div');
        marker.className = 'marker';
        marker.style.backgroundImage = `url('/img/barrier_icons_large/${type || 'unknown'}.svg')`;
        marker.addEventListener('mouseleave', () => {
          this.marker.remove();
        });
        marker = new mapboxgl.Marker(marker);
        return marker;
      };

      this.map.on('mouseenter', 'amber', (e) => {
        if (this.marker) {
          this.marker.remove();
        }
        let coordinates = e.features[0].geometry.coordinates;
        this.marker = markerMaker(e.features[0].properties.type);
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        this.marker.setLngLat(coordinates).addTo(this.map);
      });

    });

    // Wait for styles to load, and replace map layer; only do once
    let layerRemoved = false;
    this.map.on('style.load', () => {
      if (!layerRemoved) {
        this.generateMapLayer();
        layerRemoved = true;
      }
      this.refreshBoundaries();
      this.switchRaster(this.rasterMode, true);
      // this.map.setLayoutProperty('atlas-density', 'visibility', 'visible');
    });

    // Generate popup when clicking on barrier
    this.map.on('click', 'amber', (e) => {
      const feature = e.features[0];
      this.popUp = new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
        .setLngLat(feature.geometry.coordinates);
      const html = this.generatePopupContents(e.features[0].properties);
      const lastPopup = this.popUp;
      this.popUp.setHTML(html);
      this.popUp.addTo(this.map);

      this.api.getTypeResults(feature.properties.id)
        .subscribe(data => {
          const index = data.labels.indexOf(feature.properties.type);
          const total = data.data.reduce((a, b) => a + b, 0);
          let confidence = null;
          if (index !== -1) {
            confidence = Math.floor((data.data[index] / total) * 100);
          }

          if (confidence !== null) {
            const newHtml = this.generatePopupContents(feature.properties, confidence, total);
            lastPopup.setHTML(newHtml);
          }
          const downloadButton = document.getElementById('popup-download-btn');
          downloadButton.addEventListener('click', () => this.downloadRecord(feature.properties.id));
        });
    });

    this.map.on('click', 'regions', (e) => {
      if (e.features.length === 0) {
        return;
      }
      const feature = e.features[0];

      const id = feature.properties[this.REGION_ID_PATH];

      if (this.selectedBoundaries[id]) { // already selected
        delete this.selectedBoundaries[id];
      } else {
        this.selectedBoundaries[id] = true;
      }

      const sourceFeatures = (this.highlightsSource.data as any).features;

      const featureIndex = sourceFeatures.findIndex(f => f.properties[this.REGION_ID_PATH] === id);
      if (featureIndex !== -1) { // feature exists, deselect
        sourceFeatures.splice(featureIndex, 1);
        if (!this.map.getSource('highlights')) {
          return;
        }
        (this.map.getSource('highlights') as mapboxgl.GeoJSONSource).setData(this.highlightsSource.data);
      } else { // select
        this.api.getCollectionItemGeometry(this.REGIONS_COLLECTION_ID, id).subscribe(geometry => {
          sourceFeatures.push(Object.assign(feature, { geometry }));
          if (!this.map.getSource('highlights')) {
            return;
          }
          (this.map.getSource('highlights') as mapboxgl.GeoJSONSource).setData(this.highlightsSource.data);
        });
      }
    });

    this.map.on('click', 'countries', (e) => {
      if (e.features.length === 0) {
        return;
      }
      const feature = e.features[0];
      const id = feature.properties[this.COUNTRY_PATH];
      if (this.selectedBoundaries[id]) { // already selected
        delete this.selectedBoundaries[id];
      } else {
        this.selectedBoundaries[id] = true;
      }

      const sourceFeatures = (this.highlightsSource.data as any).features;

      const featureIndex = sourceFeatures.findIndex(f => f.properties[this.COUNTRY_PATH] === id);
      if (featureIndex !== -1) { // feature exists, deselect
        sourceFeatures.splice(featureIndex, 1);
        if (!this.map.getSource('highlights')) {
          return;
        }
        (this.map.getSource('highlights') as mapboxgl.GeoJSONSource).setData(this.highlightsSource.data);
      } else { // select
        this.api.getCollectionItemGeometry(this.COUNTRIES_COLLECTION_ID, id).subscribe(geometry => {
          sourceFeatures.push(Object.assign(feature, { geometry }));
          if (!this.map.getSource('highlights')) {
            return;
          }
          (this.map.getSource('highlights') as mapboxgl.GeoJSONSource).setData(this.highlightsSource.data);
        });
      }
    });

    this.map.addControl(new mapboxgl.NavigationControl());
    this.map.dragRotate.disable();
    this.map.dragPan.enable();

    // Handle zooming; only zoom with mouse wheel when Ctrl/Cmd key pressed

    const scrollHandler = (e) => {
      if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
        return;
      }
      e.preventDefault(); // prevent non metakey scroll zoom
    };
    this.map.on('wheel', scrollHandler);
    this.map.on('mousewheel', scrollHandler);

    this.mapService.map = this.map;
  }

  generateMapLayer() {
    // Remove old layer
    if (this.map.getLayer('amber')) {
      this.map.removeLayer('amber');
      this.map.removeSource('amber');
    }
    const tileUrl = 'https://maps.coreo.io/projects/barrier-tracker/source/r26/{z}/{x}/{y}.mvt';

    this.map.addSource('amber', {
      type: 'vector',
      tiles: [
        tileUrl
      ]
    });

    // Add source as layer
    this.map.addLayer({
      id: 'amber',
      source: 'amber',
      type: 'circle',
      'source-layer': `records-26`,
      paint: {
        'circle-radius': {
          'base': 1,
          'stops': [
            [4.5, 4],
            [14, 10]
          ]
        },
        'circle-color': [
          'match',
          ['get', 'type'],
          'weir', '#258ed0',
          'dam', '#4ab389',
          'culvert', '#ffee64',
          'ford', '#e5652d',
          'sluice_gate', '#e52c2d',
          'ramp', '#9f41f7',
          'other', '#f7f7f7',
          '#f7f7f7'
        ]
      }
    });

    this.map.addSource('highlights', this.highlightsSource);

    this.map.addLayer({
      id: 'highlighting',
      source: 'highlights',
      type: 'fill',
      paint: {
        'fill-color': 'rgba(62, 216, 240, 0.5)'
      }
    });


    // Swap visibility if atlas toggle is already switched
    if (this.atlasLayerIsOn) {
      this.map.setLayoutProperty('amber', 'visibility', 'none');
      this.setAtlasLayersVisibility();
    }

    // Apply any filters
    this.mapboxFilter();
  }

  // Generate map popup contents from available data
  generatePopupContents(properties: any, confidence: number = null, total: number = null) {
    let confidenceText = '';
    if (confidence !== null) {
      confidenceText = `<span class="confidence">${confidence}% confidence</span>
            <span class="confidence">${total} classification${total !== 1 ? 's' : ''}</span>`;
    }

    return `
            <div class="info-image">
                <img src="${this.coreo.photoUrl(properties.id, 'photo', 150, 200)}" />
            </div>
            <div class="type-holder">
                <img src="/img/barrier_icons_large/${properties.type || 'unknown'}.svg" class="type-icon" />
                <div class="popup-barrier-type">
                    <span>${this.barrierNamePipe.transform(properties.type || 'Unknown')}</span>
                    ${(confidenceText)}
                </div>
                <div id="popup-download-btn" style="margin-left: auto;" title="Download this record">
                  <i id="popup-download-btn-icon" class="icon-download"></i>
                </div>
            </div>
            <dl style="margin:none">
                <dt style="border:none">Date:</dt>
                <dd style="border:none">${this.datePipe.transform(properties.created_at)}</dd>
            </dl>
            <a href="/barriers/?id=${properties.id}" target="_blank">Read more...</a>
        `;
  }

  // Apply selected filters to all map layers
  async mapboxFilter() {
    let filter: any = [['!=', 'verificationState', 'Incorrect']];
    let filterAtlas: any = [['!=', 'verificationState', 'Incorrect']];
    const barrierTypesFilter = await this.store.select(getFilterTypes).take(1).toPromise();
    const selectedCountry = await this.store.select(getFilterCountry).take(1).toPromise();
    for (let type of this.filterTypes) {
      if (!barrierTypesFilter[type.slug]) {
        filter.push(['!=', 'type', type.slug]);
      }
    }

    if (!selectedCountry) {
      filter.push(['has', 'country']);
    } else {
      filter.push(['==', 'country', selectedCountry]);
    }

    filter.unshift('all');
    filterAtlas.unshift('all');

    this.map.setFilter('amber', filter);
    for (let type of barrierTypes) {
      this.map.setFilter(`atlas-${this.atlasTypes[type.slug]}`, filterAtlas);
    }
    /* Atlas data filter, uncomment if the types changed  */
    // this.map.setFilter('atlas', filter);
  }

  toggleStylesMenu() {
    this.stylesMenuIsOn = !this.stylesMenuIsOn;
  }

  toggleLayerMenu() {
    this.layerMenuIsOn = !this.layerMenuIsOn;
  }

  switchRaster(mode: rasterMode, force = false) {
    const rasterTypes = ['atlas', 'model'];
    if (mode === this.rasterMode && !force) {
      return;
    }

    this.rasterMode = mode;

    for (const type of rasterTypes) {
      if (mode !== type) {
        this.map.setLayoutProperty(type + '-density', 'visibility', 'none');
      }
    }

    if (mode === 'none') {
      return;
    }

    this.map.setLayoutProperty(mode + '-density', 'visibility', 'visible');
  }

  // Swap between street/satellite/hybrid maps
  switchStyle(style: mapStyle) {
    const changeMap = link => {
      this.map.remove();

      this.map = new mapboxgl.Map({
        container: 'map',
        style: link,
        center: [-2, 52.1],
        zoom: 4.01,
        minZoom: 4.01,
        scrollZoom: true,
        attributionControl: false,
      });
      this.initializeMap();
    };

    const styles = {
      streets: () => {
        changeMap('mapbox://styles/natapp/ck629fnm00upz1imwyk3i2l46');
        this.selectedMapStyle = 'streets';
      },
      satellite: () => {
        changeMap('mapbox://styles/natapp/ck6299lsj0uo51ilowk1xu56k');
        this.selectedMapStyle = 'satellite';
      },
      hybrid: () => {
        changeMap('mapbox://styles/natapp/ck60s5k10010f1irviujez8sq');
        this.selectedMapStyle = 'hybrid';
      }
    };

    styles[style]();
  }

  // Swap between AMBER/Atlas data
  switchLayer(layer: string) {
    this.atlasLayerIsOn = !this.atlasLayerIsOn;
    switch (layer) {
      case 'app': {
        this.map.setLayoutProperty('amber', 'visibility', 'visible');
        for (let type of barrierTypes) {
          this.map.setLayoutProperty(`atlas-${this.atlasTypes[type.slug]}`, 'visibility', 'none');
        }
        this.atlasLayerIsOn = false;
      }
        break;
      case 'atlas': {
        this.map.setLayoutProperty('amber', 'visibility', 'none');
        this.setAtlasLayersVisibility();
        this.atlasLayerIsOn = true;
      }
        break;
    }
  }

  switchLeaderboard(mode: leaderboardMode) {
    this.leaderboardMode = mode;
  }

  async setAtlasLayersVisibility() {
    const barrierTypesFilter = await this.store.select(getFilterTypes).take(1).toPromise();
    for (let type of barrierTypes) {
      if (!barrierTypesFilter[type.slug]) {
        this.map.setLayoutProperty(`atlas-${this.atlasTypes[type.slug]}`, 'visibility', 'none');
      } else if (barrierTypesFilter[type.slug]) {
        this.map.setLayoutProperty(`atlas-${this.atlasTypes[type.slug]}`, 'visibility', 'visible');
      }
    }
  }

  showRecent(item) {
    if (this.lastOpened) {
      this.lastOpened.close();
    }
    if (this.popUp) {
      this.popUp.remove();
    }
    this.map.flyTo({
      center: [item.lng, item.lat],
      zoom: 13,
      speed: 1.2
    });
    this.showReset = true;
  }

  resetZoom() {
    this.showReset = false;
    if (this.lastOpened) {
      this.lastOpened.close();
    }
    if (this.popUp) {
      this.popUp.remove();
    }
    this.selected = null;
    this.map.flyTo({
      center: [-2, 52.1],
      zoom: 4.01
    });

  }

  toggleFilter(slug: string) {
    this.store.dispatch(new ToggleType(slug));
  }

  toggleFilterOptions() {
    this.showFilters = !this.showFilters;
  }

  changeCountry(country: string) {
    this.store.dispatch(new UpdateCountry(country));
  }

  refreshBoundaries() { // called on map style update since layers need re-enabling
    if (this.boundaryMode === 'none') {
      return;
    }
    const currentMode = this.boundaryMode;
    this.changeBoundaryMode('none', true);
    this.changeBoundaryMode(currentMode, true);
  }

  changeBoundaryMode(mode: boundaryMode, preserveHighlights = false) {
    const ALL_BOUNDARY_MODES = ['regions', 'countries', 'none'];
    if (this.boundaryMode === mode) {
      return;
    }

    this.boundaryMode = mode;
    if (!preserveHighlights) { // clear highlighted features, for when swapping between map layers
      this.selectedBoundaries = {};
      (this.highlightsSource.data as any).features = [];
      if (this.map.getSource('highlights')) {
        (this.map.getSource('highlights') as mapboxgl.GeoJSONSource).setData(this.highlightsSource.data);
      }
    }

    // update layers
    for (const boundaryMode of ALL_BOUNDARY_MODES) {
      if (mode === boundaryMode) {
        if (boundaryMode !== 'none') {
          this.map.setLayoutProperty(boundaryMode, 'visibility', 'visible');
          this.map.setLayoutProperty(boundaryMode + '-lines', 'visibility', 'visible');
          this.map.setPaintProperty(boundaryMode + '-lines', 'line-color', 'rgba(162, 89, 235, 0.7)');
          this.map.setPaintProperty(boundaryMode + '-lines', 'line-width', 2);
        }
      } else {
        if (boundaryMode !== 'none') {
          this.map.setLayoutProperty(boundaryMode, 'visibility', 'none');
          this.map.setLayoutProperty(boundaryMode + '-lines', 'visibility', 'none');
        }
      }
    }
  }

  downloadRecord(recordId) {
    return this.api.getRecordById(recordId).subscribe(record => {
      const headers = ['id', 'lat', 'lng'];
      const row = [];

      row.push(record.id, record.lat, record.lng);

      for (const path of Object.keys(record.data)) {
        headers.push(path);
        row.push(record.data[path]);
      }

      const csv = headers.join(',') + '\n' + row.join(',');

      if (window.Blob === undefined || window.URL === undefined || window.URL.createObjectURL === undefined) {
        alert('Your browser doesn\'t support Blobs');
        return;
      }
      const file = new Blob([csv], { type: 'text/csv' });
      const link = document.createElement('a');
      link.download = 'record_' + record.id + '.csv';
      link.href = window.URL.createObjectURL(file);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
    });
  }

  openDialog(id) {
    const DIALOG_IDS = ['map-dialog-settings', 'map-dialog-leaderboards', 'map-dialog-downloads'];
    for (const dialogId of DIALOG_IDS) {
      const el = document.getElementById(dialogId);
      if (dialogId === id && el.style.display !== 'inline-block') {
        el.style.display = 'inline-block';
      } else {
        el.style.display = 'none';
      }
    }
  }

  async exportData(exportAll = false) {
    let query = '?';
    let firstParam = false;

    let generateExportUrl;

    const barrierTypesFilter = await this.store.select(getFilterTypes).take(1).toPromise();

    // Are all boundaries currently being displayed?
    const allBoundaries = Object.keys(barrierTypesFilter)
      .reduce((acc, t) => acc + (!!barrierTypesFilter[t] ? 1 : 0), 0) === this.filterTypes.length;

    if ((Object.keys(this.selectedBoundaries).length === 0 && allBoundaries) || exportAll) {
      generateExportUrl = this.PROJECT_EXPORT_URL;
    } else {
      for (const boundaryId of Object.keys(this.selectedBoundaries)) {
        if (firstParam) {
          firstParam = false;
        } else {
          query += '&';
        }
        query += this.boundaryMode + '[]=' + boundaryId;
      }
      if (!allBoundaries) {
        for (const barrierType of this.filterTypes) {
          if (!barrierTypesFilter[barrierType.slug]) {
            continue;
          }
          if (firstParam) {
            firstParam = false;
          } else {
            query += '&';
          }
          query += 'types[]=' + barrierType.slug;
        }
      }


      generateExportUrl = this.BOUNDARIES_EXPORT_FUNCTION_URL + query;
    }

    this.isGeneratingDownload = true;

    const exportUrlRequest = this.http.get(generateExportUrl);

    exportUrlRequest.subscribe(resp => {
      this.isGeneratingDownload = false;
      const downloadUrl = resp.text();
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
    });
  }

  onDownloadForm() {
    this.hasSubmittedDownloadDetails = true;
    this.showDownloadThanks = true;
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/amber-map/amber-map.component.ts